/** 樓層輸入解析。表單的自訂樓層欄位（「其他」/「6F+」）與預設選項共用此邏輯。 */

/**
 * 全形 → 半形：ASCII 可見字元（Ｂ２ 等，U+FF01–FF5E）轉回 U+0021–007E，全形空白轉一般空白。
 * 中文輸入法在全形模式下輸入的「Ｂ２」「７」會卡住 ASCII-only 的正規表示式，先正規化避免誤判格式錯誤。
 */
const toHalfWidth = (s: string): string =>
  s.replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)).replace(/　/g, " ");

// 觸發自訂樓層輸入的選項值：GasShop 用「其他」，Kiosk 用「6F+」。
const CUSTOM_FLOOR_VALUES = new Set(["其他", "6F+"]);

// 中文數字 → 阿拉伯數字（樓層常見範圍，支援 0–99）。
const CJK_DIGIT: Record<string, number> = {
  "〇": 0,
  零: 0,
  一: 1,
  二: 2,
  兩: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

/** 把「數字部分」轉成整數：阿拉伯（含負號）或中文數字（一、十二、二十一…）；無法解析回 null。 */
const numberPartToInt = (s: string): number | null => {
  if (/^-?\d+$/.test(s)) return Number(s);
  // 含「十」：十=10、十一=11、二十=20、二十一=21…（須在單字判斷前處理，否則「十」會被當成非數字）
  const ti = s.indexOf("十");
  if (ti !== -1) {
    const before = s.slice(0, ti);
    const after = s.slice(ti + 1);
    const tens = before === "" ? 1 : (before in CJK_DIGIT ? CJK_DIGIT[before] : null);
    const units = after === "" ? 0 : (after in CJK_DIGIT ? CJK_DIGIT[after] : null);
    if (tens === null || units === null) return null;
    return tens * 10 + units;
  }
  if (s.length === 1) return s in CJK_DIGIT ? CJK_DIGIT[s] : null;
  return null;
};

/**
 * 樓層表單值 → BE int。
 * - 預設選項：B1 → -1、1F → 1。
 * - 自訂（floor 為「其他」或「6F+」）：讀 floorOther，接受多種寫法：
 *     地上 → 「5」「5F」「5樓」「一樓」「十二樓」；
 *     地下 → 「B2」「地下2」「地下二樓」（前綴 B/地下、可帶「樓」後綴）。
 *   會先把全形正規化成半形、去掉「樓/F」後綴與「B/地下」前綴，數字部分支援阿拉伯與中文；
 *   格式不符回 null（交由表單驗證提示）。
 */
export function parseFloorToInt(floor: string, floorOther?: string): number | null {
  if (CUSTOM_FLOOR_VALUES.has(floor)) {
    if (!floorOther) return null;
    let s = toHalfWidth(floorOther).trim().toUpperCase();
    if (!s) return null;

    let sign = 1;
    const basement = /^(?:B|地下)(.+)$/.exec(s); // 地下：B<n> / 地下<n>
    if (basement) {
      sign = -1;
      s = basement[1];
    }
    s = s.replace(/(?:樓|F)$/, ""); // 去掉「樓 / F」後綴
    if (s === "") return null;

    const n = numberPartToInt(s);
    return n === null ? null : sign * n;
  }
  if (floor.startsWith("B")) return -Number(floor.slice(1));
  return Number(floor.replace(/F$/i, ""));
}
